import { useTranslation } from 'react-i18next';
import { Badge, Card, Group, Stack, Text } from '@mantine/core';
import { IconBuilding, IconClipboardCheck, IconCoin, IconMessage, IconScale } from '@tabler/icons-react';
import SectionLabel from '@/components/common/SectionLabel';
import TermRow from '@/components/common/TermRow';
import { decodeEntities, formatMoney, formatOrgName } from '@/lib/format';
import type { PurchasePriceList } from '@/types/buy';
import IdText from '@/components/common/IdText';

// Метки зачёта качества (enum бэкенда). Неизвестное значение показываем как есть (не падаем).
const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.buy.terms.creditAverage',
  BY_EACH_DAY: 'app.buy.terms.creditEachDay',
};

// «Условия предложения» — порядок строк ЗЕРКАЛИТ форму редактирования (требование):
// зачёт качества → приёмка → грузополучатель → прочие условия → комиссия.
// Данные приходят в prices_list, дополнительных запросов нет.
export default function OfferTerms({ list }: { readonly list: PurchasePriceList }) {
  const { t } = useTranslation();
  const creditKey = list.qualityCreditType ? CREDIT_TYPE_KEYS[list.qualityCreditType] : undefined;
  const acceptance = list.qualityAcceptanceProperties ?? [];

  return (
    <Stack gap="sm">
      <SectionLabel>{t('app.buy.terms.title')}</SectionLabel>
      <Card withBorder padding="lg">
        <Stack gap="md">
          {list.qualityCreditType ? (
            <TermRow icon={<IconScale size={18} />} label={t('app.buy.terms.credit')}>
              <Text size="sm" c="gray.8">
                {creditKey ? t(creditKey) : list.qualityCreditType}
              </Text>
            </TermRow>
          ) : null}

          <TermRow icon={<IconClipboardCheck size={18} />} label={t('app.buy.terms.acceptance')}>
            <Stack gap={6}>
              {list.qualityAcceptanceByGost ? (
                <>
                  <Text size="sm" c="gray.8">
                    {t('app.buy.terms.acceptanceGost')}
                  </Text>
                  {acceptance.length > 0 ? (
                    <Group gap={4}>
                      {acceptance.map((property) => (
                        <Badge
                          key={property.name}
                          variant="light"
                          color="gray"
                          tt="none"
                          fw={500}
                          size="lg"
                          radius="sm"
                        >
                          {property.name}
                          {property.gostValue ? ` ${decodeEntities(property.gostValue)}` : ''}
                        </Badge>
                      ))}
                    </Group>
                  ) : null}
                </>
              ) : (
                // Не по ГОСТу: согласованные правила «Если X превышает N% → снижение веса на M%».
                <Stack gap={2}>
                  {acceptance.map((property) => (
                    <Text key={property.name} size="sm" c="gray.8">
                      {t('app.buy.form.qaRuleIf')}{' '}
                      <Text component="span" fw={600} c="gray.9">
                        {property.name}
                      </Text>{' '}
                      {t('app.buy.form.qaRuleExceeds')} {property.increaseValue}% —{' '}
                      {t('app.buy.form.qaRuleThen')} {property.decreaseWeightValue}%
                    </Text>
                  ))}
                </Stack>
              )}
            </Stack>
          </TermRow>

          {list.consigneeName ? (
            <TermRow icon={<IconBuilding size={18} />} label={t('app.buy.form.consignee')}>
              <Stack gap={2}>
                <Text size="sm" c="gray.9" fw={500}>
                  {formatOrgName(list.consigneeName)}
                </Text>
                <Text size="sm" c="gray.8">
                  {list.consigneeInn ? (
                    <>
                      {t('auth.fields.inn')} <IdText>{list.consigneeInn}</IdText>
                    </>
                  ) : null}
                  {list.consigneeInn && list.consigneeKpp ? ' · ' : null}
                  {list.consigneeKpp ? (
                    <>
                      {t('app.company.card.kpp')} <IdText>{list.consigneeKpp}</IdText>
                    </>
                  ) : null}
                </Text>
              </Stack>
            </TermRow>
          ) : null}

          {list.userComment ? (
            <TermRow icon={<IconMessage size={18} />} label={t('app.buy.terms.comment')}>
              <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>
                {list.userComment}
              </Text>
            </TermRow>
          ) : null}

          {list.reward ? (
            <TermRow icon={<IconCoin size={18} />} label={t('app.buy.terms.commission')}>
              <Text size="sm" c="gray.8">
                {t('app.buy.terms.commissionValue', { value: formatMoney(list.reward) })}
              </Text>
            </TermRow>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
